import { normalizeHotkey } from "@tanstack/react-hotkeys";
import { createStore } from "@xstate/store";
import { findHotkeyConflict } from "./hotkey-conflict";
import { commandMap } from "./registry";

const STORAGE_KEY = "hotkey-bindings";

/** Map of commandId → user-assigned hotkey string. Null = explicitly unbound. */
export type HotkeyBindings = Record<string, string | null>;

function loadBindings(): HotkeyBindings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as HotkeyBindings) : {};
  } catch {
    return {};
  }
}

function saveBindings(bindings: HotkeyBindings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
}

/** Resolve effective hotkey: user override > default > null */
export function resolveHotkey(commandId: string, bindings: HotkeyBindings): string | null {
  if (commandId in bindings) return bindings[commandId];
  const cmd = commandMap.get(commandId);
  return cmd?.defaultHotkey ?? null;
}

export const hotkeyStore = createStore({
  context: {
    bindings: loadBindings(),
  },
  on: {
    "hotkey.set": (ctx, e: { commandId: string; hotkey: string | null }) => ({
      bindings: { ...ctx.bindings, [e.commandId]: e.hotkey },
    }),
    "hotkey.reset": (ctx, e: { commandId: string }) => {
      const next = { ...ctx.bindings };
      delete next[e.commandId];
      return { bindings: next };
    },
    "hotkey.resetAll": () => ({
      bindings: {} as HotkeyBindings,
    }),
  },
});

hotkeyStore.subscribe(() => {
  saveBindings(hotkeyStore.get().context.bindings);
});

/**
 * Record a chord for a command. Rejected outright (no write) when the chord
 * conflicts with another command's effective binding — returns the label of
 * the conflicting command in that case. A chord equal to the command's
 * default stores no override; otherwise (including an explicit `null` clear)
 * stores an override. Returns null when the chord was committed.
 */
export function recordHotkey(commandId: string, chord: string | null): string | null {
  const { bindings } = hotkeyStore.get().context;
  const conflict = findHotkeyConflict(chord, commandId, bindings);
  if (conflict) return conflict;

  const cmd = commandMap.get(commandId);
  const defaultHotkey = cmd?.defaultHotkey ?? null;
  if (chord !== null && defaultHotkey !== null && normalizeHotkey(chord) === normalizeHotkey(defaultHotkey)) {
    hotkeyStore.send({ type: "hotkey.reset", commandId });
  } else {
    hotkeyStore.send({ type: "hotkey.set", commandId, hotkey: chord });
  }
  return null;
}
